/**
 * Parse location from SKU when location field is not set
 *
 * Supports the following patterns:
 * - #<numbers> - e.g., "#04874 10865-646" => "#04874"
 * - SO<number> - e.g., "SO105" or "SO105-something" => "SO105"
 * - P#<number> - e.g., "P#1" or "P#1-item" => "P#1"
 *
 * @param {String} sku - The SKU string to parse
 * @returns {String|null} - The extracted location or null if no pattern matches
 */
export function parseLocationFromSKU(sku) {
  if (!sku || typeof sku !== 'string') {
    return null;
  }

  // Pattern 1: #<numbers> (e.g., "#04874 10865-646")
  // Match # followed by one or more digits
  const hashPattern = /#(\d+)/;
  const hashMatch = sku.match(hashPattern);
  if (hashMatch) {
    return `#${hashMatch[1]}`;
  }

  // Pattern 2: SO<number> (e.g., "SO105" or "SO105-646")
  // Match SO followed by one or more digits
  const soPattern = /SO(\d+)/i; // Case insensitive
  const soMatch = sku.match(soPattern);
  if (soMatch) {
    return `SO${soMatch[1]}`;
  }

  // Pattern 3: P#<number> (e.g., "P#1" or "P#1-item")
  // Match P# followed by one or more digits
  const pHashPattern = /P#(\d+)/i; // Case insensitive
  const pHashMatch = sku.match(pHashPattern);
  if (pHashMatch) {
    return `P#${pHashMatch[1]}`;
  }

  // No pattern matched
  return null;
}

/**
 * Get the effective location for an inventory item
 * Uses the location field if available, otherwise attempts to parse from SKU
 *
 * @param {Object} item - The inventory item with sku and location properties
 * @returns {String} - The effective location (parsed or 'Unknown')
 */
export function getEffectiveLocation(item) {
  // If location is already set and not empty, use it
  if (item.location && item.location.trim()) {
    return item.location.trim();
  }

  // Try to parse from SKU
  const parsedLocation = parseLocationFromSKU(item.sku);
  if (parsedLocation) {
    return parsedLocation;
  }

  // No location found
  return 'Unknown';
}

/**
 * Check if a search pattern matches an item's location or SKU
 *
 * @param {Object} item - The inventory item
 * @param {String} pattern - The search pattern (e.g., "#059", "SO105")
 * @returns {Boolean} - True if pattern matches location or SKU
 */
export function matchesLocationOrSKUPattern(item, pattern) {
  if (!pattern || !pattern.trim()) {
    return true; // No pattern means show all
  }

  const normalizedPattern = pattern.trim().toLowerCase();

  // Get effective location
  const effectiveLocation = getEffectiveLocation(item);

  // Check if pattern matches location (case insensitive)
  if (effectiveLocation && effectiveLocation.toLowerCase().includes(normalizedPattern)) {
    return true;
  }

  // Check if pattern matches SKU (case insensitive)
  if (item.sku && item.sku.toLowerCase().includes(normalizedPattern)) {
    return true;
  }

  return false;
}
